// src/Components/Home/NewsCarousel.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Slider from 'react-slick';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';

// Icons
import { MenuResetView, Grid5x5Icon } from '../../assets/icons/CustomIconsAdd4';

import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../Firebase/firebase';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export default function NewsCarousel() {
  const [slides, setSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Layout, Dragging & Sizing States
  const [containerPos, setContainerPos] = useState({ x: 10, y: 150 });

  // Calculate initial size to prevent bleeding off small screens
  const [containerSize, setContainerSize] = useState(() =>
    Math.min(800, window.innerHeight - 160),
  );

  const [isDragging, setIsDragging] = useState(false);
  const dragInfo = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
  });

  const resizeInfo = useRef({ isResizing: false, startX: 0, startY: 0, initialSize: 0 });
  const sliderRef = useRef(null);

  // --- FETCH NEWS DATA ---
  useEffect(() => {
    const q = collection(db, 'newsPortfolio');
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const fetchedSlides = [];

        querySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Only include items that have successfully generated storage URLs
          if (data.pdfThumbnailUrl && data.pdfDownloadUrl) {
            fetchedSlides.push({
              id: docSnap.id,
              name: data.name || data.title || 'Newsfeed',
              pdfThumbnailUrl: data.pdfThumbnailUrl,
              pdfDownloadUrl: data.pdfDownloadUrl,
              hiresDownloadUrl: data.hiresDownloadUrl, // Fetch the new high-res URL
            });
          }
        });

        // AMENDED: Sort alphanumerically by name in reverse order (Z to A)
        fetchedSlides.sort((a, b) => (b.name || '').localeCompare(a.name || ''));

        setSlides(fetchedSlides);
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching Newsfeed items:', error);
        setIsLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const settings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      fade: true,
      cssEase: 'linear',
      speed: 1000,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      adaptiveHeight: false,
      autoplay: false, // Explicitly disabled scrolling
      pauseOnHover: false,
      accessibility: false,
      focusOnSelect: false,
      lazyLoad: 'progressive',
      useTransform: true,
      touchThreshold: 10,
      useCSS: true,
    }),
    [],
  );

  // --- DRAG HANDLERS ---
  const handlePointerDown = (e) => {
    const isDragHandle = e.target.closest('.drag-handle');
    if (!isDragHandle) {
      return;
    }

    dragInfo.current.isDragging = true;
    setIsDragging(true);
    dragInfo.current.startX = e.clientX;
    dragInfo.current.startY = e.clientY;
    dragInfo.current.initialX = containerPos.x;
    dragInfo.current.initialY = containerPos.y;

    try {
      if (e.target.setPointerCapture) {
        e.target.setPointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  const handlePointerMove = (e) => {
    if (!dragInfo.current.isDragging) return;
    setContainerPos({
      x: dragInfo.current.initialX + (e.clientX - dragInfo.current.startX),
      y: dragInfo.current.initialY + (e.clientY - dragInfo.current.startY),
    });
  };

  const handlePointerUp = (e) => {
    if (!dragInfo.current.isDragging) return;
    dragInfo.current.isDragging = false;
    setIsDragging(false);
    try {
      if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  // --- RESIZE HANDLERS ---
  const handleResizePointerDown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    resizeInfo.current.isResizing = true;
    resizeInfo.current.startX = e.clientX;
    resizeInfo.current.startY = e.clientY;
    resizeInfo.current.initialSize = containerSize;
    try {
      if (e.target.setPointerCapture) {
        e.target.setPointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  const handleResizePointerMove = (e) => {
    if (!resizeInfo.current.isResizing) return;
    e.stopPropagation();
    e.preventDefault();

    let deltaX = e.clientX - resizeInfo.current.startX;
    let deltaY = e.clientY - resizeInfo.current.startY;

    const maxDelta = Math.max(deltaX, deltaY);
    const newSize = Math.max(400, resizeInfo.current.initialSize + maxDelta);
    setContainerSize(newSize);
  };

  const handleResizePointerUp = (e) => {
    if (!resizeInfo.current.isResizing) return;
    e.stopPropagation();
    e.preventDefault();
    resizeInfo.current.isResizing = false;
    try {
      if (e.target.hasPointerCapture && e.target.hasPointerCapture(e.pointerId)) {
        e.target.releasePointerCapture(e.pointerId);
      }
    } catch (err) {}
  };

  const handleOpenPdf = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          className='image-panel'
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#f2f2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
            pointerEvents: 'auto',
          }}>
          <CircularProgress />
        </Box>
      );
    }

    if (slides.length === 0) {
      return (
        <Box
          className='image-panel'
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#f2f2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
            pointerEvents: 'auto',
          }}>
          <Typography
            variant='h6'
            sx={{ color: '#000', fontStyle: 'italic', fontWeight: 'normal' }}>
            News coming soon
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {/* IMAGE / SLIDER PANEL */}
        <Box
          className='image-panel'
          sx={{
            display: 'block',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            backgroundColor: '#f2f2f2', // Container changed to 5% faint grey
            boxShadow: '4px 4px 10px rgba(0,0,0,0.5)',
            pointerEvents: 'auto',
            overflow: 'hidden',
            '& .slick-slider': { height: '100%', width: '100%' },
            '& .slick-list': { height: '100%' },
            '& .slick-track': { height: '100%', display: 'flex', alignItems: 'center' },
            '& .slick-slide': {
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
            },
            '& .slick-slide.slick-active': { zIndex: 2 },
            '& .slick-slide > div': { height: '100%', width: '100%' },
            '& .slick-prev, & .slick-next': {
              zIndex: 10,
              width: '32px',
              height: '32px',
              display: 'flex !important',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: '1px solid #000000',
              backgroundColor: '#FFFFFF',
              opacity: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                opacity: 0.8,
              },
              '&:before': {
                content: '""',
                borderTop: '2px solid #000',
                borderLeft: '2px solid #000',
                width: '10px',
                height: '10px',
                position: 'absolute',
                opacity: 1,
              },
            },
            '& .slick-prev': {
              left: '10px',
              '&:before': {
                transform: 'rotate(-45deg)',
                marginLeft: '4px',
              },
            },
            '& .slick-next': {
              right: '10px',
              '&:before': {
                transform: 'rotate(135deg)',
                marginRight: '4px',
              },
            },
            '& .slick-dots': { bottom: '15px', zIndex: 10 },
            '& .slick-dots li button:before': { display: 'none' },
            '& .slick-dots li': { width: '14px', height: '14px', margin: '0 4px' },
            '& .slick-dots li button': {
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              padding: 0,
              border: '1px solid #000000',
              backgroundColor: '#FFFFFF',
              opacity: 0.8,
            },
            '& .slick-dots li.slick-active button': {
              border: '1px solid #000000', // Adjusted dot border for visibility on white
              backgroundColor: '#000000',
              opacity: 1,
              transform: 'scale(1.2)',
            },
          }}>
          <IconButton
            className='drag-handle'
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 100,
              color: 'black', // Updated for white background
              backgroundColor: 'rgba(255,255,255,0.5)',
              opacity: 0.5,
              cursor: 'grab',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.8)', opacity: 1 },
            }}
            title='Drag Window'>
            <Grid5x5Icon />
          </IconButton>

          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 100,
              display: 'flex',
              gap: 1,
            }}>
            <IconButton
              onClick={(e) => {
                e.currentTarget.blur();
                setContainerPos({ x: 10, y: 150 });
                setContainerSize(Math.min(800, window.innerHeight - 160));
                if (sliderRef.current) {
                  sliderRef.current.slickGoTo(0, true);
                }
              }}
              sx={{
                color: 'black', // Updated for white background
                backgroundColor: 'rgba(255,255,255,0.5)',
                opacity: 0.3,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.8)', opacity: 1 },
              }}
              title='Reset Position & Size'>
              <MenuResetView />
            </IconButton>
          </Box>

          <Slider ref={sliderRef} {...settings}>
            {slides.map((item, index) => (
              <Box
                key={item.id || index}
                tabIndex={-1}
                sx={{
                  height: '100%',
                  width: '100%',
                  position: 'relative',
                  display: 'flex !important',
                  alignItems: 'center',
                  justifyContent: 'center',
                  outline: 'none',
                }}>
                {/* AMENDED: Increased size by 10% (from 81.25% to 89%) and added bottom margin to push it upwards */}
                <img
                  src={item.hiresDownloadUrl || item.pdfThumbnailUrl}
                  alt={item.name}
                  style={{
                    maxWidth: '89%',
                    maxHeight: '89%',
                    width: 'auto',
                    height: 'auto',
                    boxShadow: '6px 6px 15px rgba(0,0,0,0.3)',
                    objectFit: 'contain',
                    marginBottom: '30px',
                  }}
                />

                {/* Overlay Button to view full PDF */}
                <Button
                  variant='contained'
                  onClick={() => handleOpenPdf(item.pdfDownloadUrl)}
                  sx={{
                    position: 'absolute',
                    bottom: 90, // Above the slider dots
                    backgroundColor: '#0056b3',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    padding: '8px 24px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    '&:hover': {
                      backgroundColor: '#004494',
                    },
                  }}>
                  View Full PDF
                </Button>
              </Box>
            ))}
          </Slider>
        </Box>

        <Box
          className='resize-handle'
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerUp}
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '30px',
            height: '30px',
            cursor: 'nwse-resize',
            zIndex: 1000,
            pointerEvents: 'auto',
            touchAction: 'none',
            background:
              'linear-gradient(135deg, transparent 50%, rgba(128,128,128,0.8) 50%)',
            '&:hover': {
              background:
                'linear-gradient(135deg, transparent 50%, rgba(128,128,128,1) 50%)',
            },
          }}
          title='Drag to resize'
        />
      </>
    );
  };

  return (
    <Portal>
      <Box
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        sx={{
          position: 'fixed',
          top: containerPos.y,
          left: containerPos.x,
          width: `${containerSize}px`,
          height: `${containerSize}px`,
          zIndex: 1600,
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          touchAction: 'none',
          transition:
            isDragging || resizeInfo.current.isResizing ? 'none' : 'all 0.3s ease-in-out',
        }}>
        {renderContent()}
      </Box>
    </Portal>
  );
}
